import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FocusLock from 'react-focus-lock';
import { data } from "../../data.js";
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
  ImageList,
  ImageListItem,
  Paper,
  Card,
  CardContent,
  Link as MuiLink
} from "@mui/material";
import { styled } from '@mui/system';

import KevinImg from "../../resources/images/external/partners/golanko-kevin.jpg";



// ---- Styled components ----

// Hero
const HeroImage = styled(Box, {
  shouldForwardProp: prop => prop !== 'src'
})(({ src }) => ({
  backgroundImage: `url(${src})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  width: '100%',
  paddingTop: '40%',
  borderRadius: 8,
  marginBottom: 24,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
}));

// full-screen overlay container:
const OverlayBox = styled(Box)(() => ({
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
  cursor: 'pointer',
  padding: 16,
  overflow: 'auto',
}));

const ProjectPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const { pid } = useParams();
  const navigate = useNavigate();
  const index = Number(pid.startsWith('20250') ? pid.slice(5) : pid);
  const project = data.projects[index];

  const [overlayImage, setOverlayImage] = useState(null);
  const [cols, setCols] = useState(3);
  const listRef = useRef(null);

  // Responsive columns
  useEffect(() => {
    const updateCols = () => setCols(window.innerWidth < 800 ? 1 : 3);
    updateCols();
    window.addEventListener('resize', updateCols);
    return () => window.removeEventListener('resize', updateCols);
  }, []);

  if (!project) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 15 }} elevation={3}>
        <Typography variant="h4" gutterBottom>Projekt archiviert</Typography>
        <Typography variant="body1" gutterBottom>Das angeforderte Projekt existiert nicht oder wurde archiviert.</Typography>
        <Button variant="contained" sx={{mt: 2}} onClick={() => navigate('/')}>Zurück zur Startseite</Button>
      </Paper>
    );
  }

  const fixPath = (path) => (path.startsWith('.') ? path.slice(1) : path);
  const pd = project.pageDetails;

  // Section anchors for nav
  const sections = [
    { id: 'hero', label: 'Überblick' },
    { id: 'details', label: 'Beschreibung' },
    { id: 'galerie', label: 'Galerie' },
    { id: 'downloads', label: 'Downloads' }
  ];

  return (
    <>
      {/* Spacer for AppBar */}
      <Box sx={{ height: (theme) => theme.mixins.toolbar.minHeight }} />

      {/* SideNav / TopNav */}
      <Box
        component="nav"
        aria-label="Seitennavigation"
        sx={theme => ({
          position: 'fixed',
          zIndex: 1100,
          top: { xs: theme.mixins.toolbar.minHeight + 15, md: '50%' },
          left: 0,
          transform: { s: 'none', md: 'translateY(-50%)' },
          width: { xs: '100%', md: 200 },
          marginLeft: { xs: '0', md: '5vw' },
          bgcolor: 'background.paper',
          borderBottom: { xs: 1, md: 0 },
          borderRight: { md: 2, color: "lightgrey" },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          overflowX: 'auto',
        })}
      >
        {sections.map(({ id, label }) => (
          <Button
            key={id}
            component="a"
            href={`#${id}`}
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById(id);
              if (el) {
                const navbarHeight = document.querySelector('.MuiAppBar-root')?.offsetHeight || 0;
                const offset = navbarHeight + 16;
                const top = el.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({ top, behavior: 'smooth' });
                el.focus({ preventScroll: true });
              }
            }}
            sx={theme => ({
              my: { md: 1 },
              mx: { xs: 1, md: 0 },
              textTransform: 'none',
              justifyContent: { xs: 'center', md: 'flex-start' },
              width: { md: '100%', xs: 'auto' },
              color: theme.palette.text.primary,
            })}
          >
            {label}
          </Button>
        ))}
      </Box>

      <Box component="main" role="main" sx={theme => ({
        ml: { md: 200, xs: 0 },
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 6 },
        maxWidth: 1200,
        mx: { md: 'auto' },
      })}>
        {/* Hero Section */}
        <HeroImage
          src={fixPath(project.titleImg)}
          role="presentation"
          aria-hidden="true"
          sx={{ marginTop: "2em" }}
        />
        <Typography id="hero" component="h1" variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'left', fontFamily: "quicksand" }}>
          {project.title}
        </Typography>

        {/* Status & Actions */}
        <Box id="status" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="subtitle1">Status: {project.status}</Typography>
          <Box>
            <Button variant="outlined" sx={{ mr: 1, textTransform: 'none', marginTop: "0.5em",bgcolor: "black", color: "white",'&:hover': { bgcolor: '#393E46' } }} onClick={() => navigate('/')}>Zurück zur Startseite</Button>
            {project.immoscout && (
              <MuiLink href={project.immoscout} target="_blank" rel="noopener noreferrer" underline="none">
                <Button variant="contained" sx={{ bgcolor: '#00ffd0', color: 'black', marginTop: "0.5em", '&:hover': { bgcolor: '#CCFFF5' }, textTransform: 'none' }}>
                  Zum Exposé auf ImmoScout24
                </Button>
              </MuiLink>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Facts Overview */}
        {pd?.overview && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {pd.overview.facts.map((f, i) => (
              <Grid item xs={6} md={2} key={i}>
                <Card elevation={1}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" component="div">{f.value}</Typography>
                    <Typography color="text.secondary" variant="body2">{f.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Quote Section */}
        {pd?.quote && (
          <Paper sx={{ p: 4, mb: 4, bgcolor: '#1b2631', color: "white" }} elevation={0}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Box
                  component="img"
                  src={fixPath(pd.quote.photo)}
                  alt={pd.quote.author}
                  sx={{ width: 80, height: 80, borderRadius: '50%' }}
                />
              </Grid>
              <Grid item xs>
                <Typography variant="h6" gutterBottom>„{pd.quote.text}“</Typography>
                <Typography variant="subtitle2">{pd.quote.author}, {pd.quote.title}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Details Section */}
        <Box id="details" sx={{ mb: 4, scrollMarginTop: (theme) => `${theme.mixins.toolbar.minHeight + 16}px` }}>
          <Typography component="h2" variant="h5" gutterBottom>Beschreibung</Typography>
          <Box component="div" dangerouslySetInnerHTML={{ __html: project.description }} sx={{ '& p': { mb: 2 } }} />
        </Box>

        {/* Ansprechpartner Kevin */}
        <Box sx={{ mb: 6, mt: 1 }}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', backgroundColor: '#f0f4f8' }}>
            <Box sx={{ width: "15em", height: "15em", flexShrink: 0, mr: { sm: 3, xs: 0 }, mb: { xs: 2, sm: 0 } }}>
              <Box
                component="img"
                src={KevinImg}
                alt="Kevin Golanko – Verkaufsleiter"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 2 }}
              />
            </Box>
            <Box>
              <Typography variant="h6"  sx={{ mb: 1 }}><b>Kevin Golanko</b></Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Ihr Ansprechpartner bei der BDG Wohnbau GmbH</Typography>
              <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
                „Ich begleite Sie persönlich durch jeden Schritt – von der ersten Besichtigung, über die individuelle Gestaltung der Objekte bis zum Notartermin. Sie haben Fragen, Wünsche oder möchten sich einfach beraten lassen? Rufen Sie mich an oder schreiben Sie mir. Ich freue mich auf Sie!“
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Telefon:</strong> <a href="tel:+49 178 6927120" style={{ textDecoration: 'none', color: '#000' }}>+49 178 6927120</a></Typography>
              <Typography variant="body2" sx={{ mb: 2 }}><strong>E-Mail:</strong> <a href="mailto:kg@golanko-immobilien.de" style={{ textDecoration: 'none', color: '#000' }}>kg@golanko-immobilien.de</a></Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" href="tel:01786927120" sx={{ textTransform: 'none', bgcolor: "#00b1d9", color: "white",'&:hover': { bgcolor: '#2cbbdb' }, mr: -0.2  }}>Direkt anrufen</Button>
                <Button variant="contained" color="primary" href="mailto:kg@golanko-immobilien.de" sx={{ textTransform: 'none' ,bgcolor: "black", color: "white",'&:hover': { bgcolor: '#393E46' } }}>E-Mail schreiben</Button>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Gallery Section */}

      <Box
        id="galerie"
        sx={{
          mb: 4,
          scrollMarginTop: theme => `${theme.mixins.toolbar.minHeight + 16}px`
        }}
      >
        <Typography component="h2" variant="h5" gutterBottom>
          Galerie
        </Typography>
        <ImageList ref={listRef} cols={cols} gap={8}>
          {project.images.map((img, i) => (
            <ImageListItem key={i} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <img
                src={fixPath(img)}
                alt={`Bild ${i + 1} von ${project.title}`}
                loading="lazy"
                onClick={() => setOverlayImage(fixPath(img))}
                style={{ cursor: 'pointer', width: '100%' }}
                tabIndex={0}
                onKeyDown={e =>
                  (e.key === 'Enter' || e.key === ' ') &&
                  setOverlayImage(fixPath(img))
                }
              />
            </ImageListItem>
          ))}
        </ImageList>

        {/* Single overlay */}
        {overlayImage && (
          <OverlayBox
            onClick={() => setOverlayImage(null)}
            onKeyDown={e => e.key === 'Escape' && setOverlayImage(null)}
            role="dialog"
            aria-modal="true"
            tabIndex={0}
          >
            <FocusLock>
              <Box
                component="img"
                src={overlayImage}
                alt={`Vergrößertes Bild von ${project.title}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  margin: 'auto',
                  display: 'block',
                }}
                onClick={e => e.stopPropagation()}
                tabIndex={0}
                onKeyDown={e =>
                  (e.key === 'Escape' || e.key === 'Enter') &&
                  setOverlayImage(null)
                }
              />
            </FocusLock>
          </OverlayBox>
        )}
      </Box>


        {/* Downloads Section */}
        {pd?.downloads && (
          <Box id="downloads" sx={{ mb: 4, scrollMarginTop: (theme) => `${theme.mixins.toolbar.minHeight + 16}px` }}>
            <Typography component="h2" variant="h5" gutterBottom>Downloads</Typography>
            {pd.downloads.map((d, i) => (
              <MuiLink key={i} href={d.url} target="_blank" rel="noopener noreferrer" underline="none" sx={{ mr: 2, mb: 1 }}>
                <Button component="a">
                  {d.label}
                </Button>
              </MuiLink>
            ))}
          </Box>
        )}

        {/* Overlay */}
        {overlayImage && (
          <OverlayBox
            onClick={() => setOverlayImage(null)}
            onKeyDown={e => e.key === 'Escape' && setOverlayImage(null)}
            tabIndex={0}
            role="dialog"
            aria-modal="true"
          >
            <FocusLock>
              <Box
                component="img"
                src={overlayImage}
                alt={`Vergrößertes Bild von ${project.title}`}
                sx={{ maxWidth: '90%', maxHeight: '90%', borderRadius: 1, boxShadow: 3 }}
                onClick={e => e.stopPropagation()}
                tabIndex={0}
                onKeyDown={e => (e.key === 'Escape' || e.key === 'Enter') && setOverlayImage(null)}
              />
            </FocusLock>
          </OverlayBox>
        )}
      </Box>
    </>
  );
};

export default ProjectPage;
